export function kiteBogoItems(items, kiteData) {
  const count = items.reduce((sum, item) => sum + item.quantity, 0);
  if (count === 1) return items;
  if (![4, 6].includes(count) || items.some(item => !Number.isInteger(item.quantity) || item.quantity < 1)) throw new Error('Choose four or six pairs for this offer.');
  const variantIds = rule => (rule?.rulesList || []).flatMap(entry => (entry.ruleValue?.value || []).flatMap(product => (product.variants || []).map(variant => String(variant.variantId))));
  const matches = (kiteData?.bogoV2 || []).flatMap(campaign => {
    if (!campaign.isEnabled || campaign.isTestMode || campaign.isCampaignUrlEnabled || campaign.mode !== 'AUTOMATIC' || campaign.advancedSettings?.onlyUseWidget) return [];
    return (campaign.segmentsData?.segmentsList || []).filter(segment =>
      segment.discountType === 'PERCENTAGE' && Number(segment.discountValue) === 100 &&
      Number(segment.buyXRulesData?.itemCount) === 1 && Number(segment.getYRulesData?.itemCount) === 1 &&
      !segment.isAutoAdd && campaign.multiplyFreeGiftWithTrigger && Number(segment.discountableSetLimit) >= count / 2 &&
      items.every(item => variantIds(segment.buyXRulesData).includes(String(item.id)) && variantIds(segment.getYRulesData).includes(String(item.id)))
    ).map(segment => ({ campaign, segment }));
  });
  if (matches.length !== 1) throw new Error('The BOGO offer is temporarily unavailable. Please refresh and try again.');
  const { campaign, segment } = matches[0];
  if (!campaign.title || !segment.segmentId) throw new Error('The BOGO offer is temporarily unavailable. Please refresh and try again.');
  let paidRemaining = count / 2;
  return items.flatMap(item => {
    const paid = Math.min(paidRemaining, item.quantity);
    paidRemaining -= paid;
    const free = item.quantity - paid;
    return [
      ...(paid ? [{ ...item, quantity: paid }] : []),
      ...(free ? [{ ...item, quantity: free, properties: { ...item.properties, _free_product: 'true', _rule_id: segment.segmentId, _kite_promo_name: campaign.title } }] : []),
    ];
  });
}

export function activeKiteData() {
  return window.gfg?.settings || window.kite_app_data || JSON.parse(document.getElementById('kite-app-data')?.textContent || '{}');
}

export function bogoTransfer(cart, data) {
  const everyday = cart.items.filter(item => item.handle === 'everyday-socks' && !item.selling_plan_allocation);
  if (!everyday.length) return null;
  const ids = rule => (rule?.rulesList || []).flatMap(entry => (entry.ruleValue?.value || []).flatMap(product => (product.variants || []).map(v => String(v.variantId))));
  const matches = (data?.bogoV2 || []).flatMap(campaign => {
    if (!campaign.isEnabled || campaign.isTestMode || campaign.isCampaignUrlEnabled || campaign.mode !== 'AUTOMATIC' || campaign.advancedSettings?.onlyUseWidget || !campaign.multiplyFreeGiftWithTrigger) return [];
    return (campaign.segmentsData?.segmentsList || []).filter(segment =>
      segment.discountType === 'PERCENTAGE' && Number(segment.discountValue) === 100 && !segment.isAutoAdd &&
      Number(segment.buyXRulesData?.itemCount) === 1 && Number(segment.getYRulesData?.itemCount) === 1 &&
      everyday.every(item => ids(segment.buyXRulesData).includes(String(item.variant_id || item.id)) && ids(segment.getYRulesData).includes(String(item.variant_id || item.id)))
    ).map(segment => ({campaign, segment}));
  });
  if (matches.length !== 1) throw new Error('The BOGO offer is unavailable. Please refresh before checking out.');
  const {campaign, segment} = matches[0];
  const isFree = item => item.properties?._free_product === 'true' && item.properties?._rule_id === segment.segmentId && item.properties?._kite_promo_name === campaign.title;
  if (everyday.some(item => item.properties?._free_product === 'true' && !isFree(item))) throw new Error('Your cart contains an older offer. Remove those pairs and add them again.');
  const total = everyday.reduce((sum,item) => sum + item.quantity, 0);
  const cap = Number(segment.discountableSetLimit) || Infinity;
  const wanted = total >= 4 ? Math.min(Math.floor(total / 2), cap) : 0;
  const actual = everyday.filter(isFree).reduce((sum,item) => sum + item.quantity, 0);
  if (wanted === actual) return null;
  const makeFree = wanted > actual;
  const source = everyday.find(item => isFree(item) !== makeFree);
  const quantity = Math.min(source.quantity, Math.abs(wanted - actual));
  const properties = {...source.properties};
  for (const key of ['_free_product','_rule_id','_kite_promo_name','_kite_uid','_latimer_bogo']) delete properties[key];
  Object.assign(properties, makeFree
    ? {_free_product:'true',_rule_id:segment.segmentId,_kite_promo_name:campaign.title}
    : {_free_product:'false',_rule_id:'',_kite_promo_name:'',_latimer_bogo:'paid'});
  return {source, quantity, properties};
}

export async function rebalanceBogo(cart, data, request) {
  for (let attempt = 0; attempt < 50; attempt++) {
    const move = bogoTransfer(cart, data);
    if (!move) return cart;
    const {source, quantity, properties} = move;
    if (quantity === source.quantity) {
      cart = await request('change', {id:source.key, quantity, properties});
    } else {
      const remaining = source.quantity - quantity;
      cart = await request('change', {id:source.key, quantity:remaining});
      try {
        await request('add', {items:[{id:source.variant_id || source.id, quantity, properties}]});
      } catch (error) {
        const restored = cart.items.find(item => (item.variant_id || item.id) === (source.variant_id || source.id) && JSON.stringify(item.properties || {}) === JSON.stringify(source.properties || {}));
        if (restored) await request('change', {id:restored.key, quantity:restored.quantity + quantity});
        else await request('add', {items:[{id:source.variant_id || source.id,quantity,properties:source.properties || {}}]});
        throw error;
      }
      cart = await request('get');
    }
  }
  throw new Error('Unable to finish updating the BOGO offer. Please refresh your cart.');
}

export function paidBogoProperties(item) {
  const properties = {...item.properties};
  for (const key of ['_free_product','_rule_id','_kite_promo_name','_kite_uid','_latimer_bogo']) delete properties[key];
  return {...properties,_free_product:'false',_rule_id:'',_kite_promo_name:'',_latimer_bogo:'paid'};
}

export async function changeBogoQuantity(lineKey, line, quantity, sections) {
  return withBogoCart(async request => {
    let cart = await request('get');
    const source = lineKey ? cart.items.find(item => item.key === lineKey) : cart.items[line - 1];
    if (!source) throw new Error('Your cart changed. Please refresh and try again.');
    if (source.handle !== 'everyday-socks' || source.selling_plan_allocation) return request('change', {id:source.key,quantity});
    const delta = quantity - source.quantity;
    const variantId = source.variant_id || source.id;
    const properties = paidBogoProperties(source);
    for (let i = 0; i < 50; i++) {
      const gift = cart.items.find(item => item.handle === 'everyday-socks' && !item.selling_plan_allocation && (item.properties?._free_product === 'true' || item.properties?._latimer_bogo !== 'paid'));
      if (!gift) break;
      cart = await request('change', {id:gift.key,quantity:gift.quantity,properties:paidBogoProperties(gift)});
    }
    if (delta > 0) await request('add', {items:[{id:variantId,quantity:delta,properties}]});
    else {
      let remaining = -delta;
      const sameProperties = item => JSON.stringify(Object.entries(paidBogoProperties(item)).sort()) === JSON.stringify(Object.entries(properties).sort());
      while (remaining > 0) {
        const item = cart.items.find(item => (item.variant_id || item.id) === variantId && !item.selling_plan_allocation && sameProperties(item));
        if (!item) throw new Error('Your cart changed. Please refresh before checking out.');
        const remove = Math.min(remaining,item.quantity);
        cart = await request('change', {id:item.key,quantity:item.quantity-remove});
        remaining -= remove;
      }
    }
    return cart;
  }, sections);
}

let bogoBusy = false;
export async function withBogoCart(action, sections = []) {
  if (bogoBusy) throw new Error('Your cart is updating. Please wait.');
  bogoBusy = true;
  const root = window.Shopify?.routes?.root || '/';
  const request = async (operation, body) => {
    const response = await fetch(root + (operation === 'get' ? 'cart.js' : `cart/${operation}.js`), body ? {method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body)} : {cache:'no-store'});
    const result = await response.json();
    if (!response.ok || result.status || result.errors) throw new Error(result.description || result.message || result.errors || 'Unable to update your cart.');
    return result;
  };
  try {
    const initial = await action(request);
    if (initial.errors) return initial;
    let cart = await rebalanceBogo(await request('get'), activeKiteData(), request);
    if (sections.length) {
      const response = await fetch(`${location.pathname}?sections=${encodeURIComponent(sections.join(','))}`, {cache:'no-store'});
      if (!response.ok) throw new Error('Unable to refresh your cart.');
      cart.sections = await response.json();
    }
    cart._latimerBogoRebalanced = true;
    return cart;
  } finally { bogoBusy = false; }
}

export function isPreorder(variant) {
  return Boolean(variant?.available && variant.inventoryTracked && variant.inventoryPolicy === 'continue' && variant.inventoryQuantity <= 0);
}
/** Latimer storefront components. All prices and availability come from Shopify variants. */
export function matchVariant(product, { size, color }) {
  return product.variants.find(variant => product.options.every((name, i) => {
    if (name.toLowerCase() === 'color' || name.toLowerCase() === 'colour') return variant.options[i] === color;
    if (name.toLowerCase() === 'size') return variant.options[i] === size;
    return product.variants.length === 1;
  }));
}
export function validMix(values, count) {
  return values.length === 3 && values.every(v => Number.isInteger(v) && v >= 0 && v <= count) && values.reduce((a, b) => a + b, 0) === count;
}
export function selectionError(product, variant, mix) {
  if (!variant) return 'Choose your size and color.';
  if (!variant.available) return 'This selection is sold out. Choose another size or color.';
  if (product.family === 'everyday' && product.count > 1 && !validMix(mix, product.count)) return `Choose exactly ${product.count} pairs across the three colors.`;
  return '';
}
export function filterColorGallery(gallery, color, colors) {
  if (!gallery) return;
  const normalize = value => String(value || '').toLowerCase().replaceAll('-', ' ').trim();
  const known = colors.map(normalize);
  const selected = normalize(color);
  const slides = Array.from(gallery.querySelectorAll('[data-slide]'));
  const hasGroups = slides.some(slide => known.includes(normalize(slide.dataset.color)));
  slides.forEach(slide => {
    const group = normalize(slide.dataset.color);
    slide.hidden = hasGroups && known.includes(group) && group !== selected;
    if (slide.hidden) slide.querySelectorAll('video').forEach(video => video.pause());
  });
  const visible = slides.filter(slide => !slide.hidden);
  gallery.querySelectorAll('[data-index]').forEach(button => {
    const slide = slides[Number(button.dataset.index)];
    button.hidden = !slide || slide.hidden;
    button.dataset.visibleIndex = visible.indexOf(slide);
  });
}
if (typeof window !== 'undefined' && typeof HTMLElement !== 'undefined') {
  window.latimerBogoCart = withBogoCart;
  window.latimerBogoChange = changeBogoQuantity;
  document.addEventListener('submit', event => {
    if (bogoBusy && event.target.matches('form[action*="/cart"]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('click', event => {
    if (bogoBusy && event.target.closest('[name="checkout"], a[href*="/checkout"], #CartDrawer-Checkout, cart-remove-button, cart-items button, cart-drawer-items button')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  class LatimerCarousel extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      const opts = { signal: this.abort.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-index], [data-next], [data-prev]');
        if (!button) return;
        const slides = this.slides;
        const next = button.hasAttribute('data-index') ? Number(button.dataset.visibleIndex ?? button.dataset.index) : (this.index + (button.hasAttribute('data-prev') ? -1 : 1) + slides.length) % slides.length;
        this.go(next);
      }, opts);
      this.track.addEventListener('scroll', () => this.update(), { ...opts, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowLeft' ? -1 : 1);
        this.go(Math.max(0, Math.min(this.slides.length - 1, next)));
      }, opts);
      document.addEventListener('shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        this.go(this.slides.indexOf(event.target.closest('[data-slide]')));
      }, opts);
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); }
    get slides() { return Array.from(this.track?.querySelectorAll('[data-slide]') || []).filter(slide => !slide.hidden); }
    get index() {
      const x = this.track.getBoundingClientRect().left;
      const distances = this.slides.map(slide => Math.abs(slide.getBoundingClientRect().left - x));
      return distances.indexOf(Math.min(...distances));
    }
    go(index, instant = false) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: this.track.scrollLeft + slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left, behavior: instant || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    update() {
      const current = this.slides[this.index];
      this.querySelectorAll('[data-index]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.visibleIndex ?? button.dataset.index) === this.index)));
      const label = this.querySelector('[data-hero-label]');
      if (label && current) label.textContent = `Everyday / ${current.dataset.label}`;
      const status = this.querySelector('[data-gallery-status]');
      if (status) status.textContent = `${this.index + 1} / ${this.slides.length}`;
      // Off-screen hero text is hidden from assistive technology; product images remain browsable.
      if (this.hasAttribute('data-hero')) this.slides.forEach(slide => slide.setAttribute('aria-hidden', String(slide !== current)));
    }
  }
  class LatimerProduct extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };
      this.data = JSON.parse(this.querySelector('[data-product-data]').textContent);
      this.data.products = [...new Map(this.data.products.map(product => [product.id, product])).values()].sort((a, b) => a.count - b.count);
      this.product = this.data.products.find(product => product.id === this.data.current);
      this.form = this.querySelector('form[action*="/cart/add"]');
      if (!this.form) return;
      this.button = this.form.querySelector('[data-add]');
      this.error = this.form.querySelector('[data-error]');
      this.native = this.form.querySelector('[name="id"]');
      this.native.disabled = true;
      this.hiddenVariant = document.createElement('input');
      Object.assign(this.hiddenVariant, { type: 'hidden', name: 'id' });
      this.form.append(this.hiddenVariant);
      this.querySelector('.lg-native-variant').hidden = true;
      this.querySelector('[data-enhanced-options]').hidden = false;
      this.querySelector('.lg-nojs-packs')?.setAttribute('hidden', '');
      this.size = this.querySelector('[data-size]');
      const requestedQuantity = Number(new URLSearchParams(window.location.search).get('quantity'));
      this.quantity = this.product.family === 'everyday' && [4, 6].includes(requestedQuantity) ? requestedQuantity : 1;
      this.everyday = this.product.family === 'everyday' && this.product.count === 1;
      this.mixInputs = Array.from(this.querySelectorAll('[data-mix-color]'));
      const selected = this.product.variants.find(v => v.id === this.data.selected);
      if (selected) this.size.value = selected.options[this.product.options.findIndex(option => option.toLowerCase() === 'size')] || '';
      const colorIndex = this.product.options.findIndex(option => ['color', 'colour'].includes(option.toLowerCase()));
      this.color = selected?.options[colorIndex] || this.product.variants[0]?.options[colorIndex] || 'Cream';
      const offerLabels = JSON.parse(this.querySelector('[data-offer-labels]').textContent);
      this.offerLabels = offerLabels;
      const packContainer = this.querySelector('[data-pack-options]');
      packContainer.replaceChildren();
      const counts = this.everyday ? [1, 4, 6] : [this.product.count];
      counts.forEach(count => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        Object.assign(input, { type: 'radio', name: 'latimer-pack', value: String(count), checked: count === this.quantity });
        const span = document.createElement('span'), title = document.createElement('strong'), small = document.createElement('small'), prices = document.createElement('span'), price = document.createElement('b');
        span.className = 'lg-offer-card'; prices.className = 'lg-offer-prices';
        const unitPrice = this.product.variants[0].price;
        title.textContent = count === 1 ? offerLabels.one : count === 4 ? offerLabels.four : offerLabels.six;
        small.textContent = count === 1 ? offerLabels.single : offerLabels.detail.replace('[count]', count).replace('[saving]', this.money(unitPrice * count / 2));
        price.textContent = this.money(unitPrice * (count === 1 ? 1 : count / 2));
        prices.append(price);
        if (count > 1) {
          const original = document.createElement('s'), badge = document.createElement('em');
          original.textContent = this.money(unitPrice * count);
          badge.className = 'lg-offer-badge'; badge.textContent = count === 4 ? offerLabels.fourBadge : offerLabels.sixBadge;
          prices.append(original); span.append(badge);
        }
        span.append(title, small, prices); label.append(input, span); packContainer.append(label);
      });
      this.querySelector('.lg-packs').hidden = !this.everyday;
      this.querySelectorAll('[name="latimer-size"]').forEach(input => { input.required = true; input.checked = input.value === this.size.value; });
      this.form.addEventListener('click', event => {
        const button = event.target.closest('[data-step]');
        if (!button || this.busy) return;
        const input = button.parentElement.querySelector('input');
        const total = this.mixValues().reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
        const delta = Number(button.dataset.step);
        if (delta > 0 && total >= this.quantity) return;
        input.value = Math.max(0, Math.min(this.quantity, Number(input.value) + delta));
        this.color = input.dataset.mixColor;
        this.update();
      }, opts);
      this.form.addEventListener('change', event => {
        if (this.busy) return;
        if (event.target.name === 'latimer-pack') {
          this.quantity = Number(event.target.value);
          this.renderOptions(true);
        }
        if (event.target.name === 'latimer-size') this.size.value = event.target.value;
        if (event.target.name === 'latimer-color') { this.color = event.target.value; this.mixInputs.forEach(input => { input.value = input.dataset.mixColor === this.color ? this.quantity : 0; }); }
        this.update();
      }, opts);
      this.mixInputs.forEach(input => input.addEventListener('input', () => this.update(), opts));
      this.form.addEventListener('submit', event => this.submit(event), opts);
      this.querySelectorAll('[data-zoom]').forEach(button => button.addEventListener('click', () => {
        const source = button.querySelector('img');
        if (!source) return;
        const dialog = this.querySelector('dialog');
        dialog.querySelector('img').src = source.currentSrc || source.src;
        dialog.querySelector('img').alt = source.alt;
        this.zoomTrigger = button; dialog.showModal();
      }, opts));
      const dialog = this.querySelector('dialog');
      dialog?.querySelector('[data-close]').addEventListener('click', () => dialog.close(), opts);
      dialog?.addEventListener('close', () => this.zoomTrigger?.focus(), opts);
      this.querySelector('[data-sticky-add]').addEventListener('click', () => {
        if (!this.purchaseError()) this.form.requestSubmit();
        else { this.form.scrollIntoView({ behavior: 'smooth', block: 'center' }); this.querySelector('[name="latimer-size"]').focus({ preventScroll: true }); }
      }, opts);
      const updateSticky = () => {
        if (this.stickyFrame) return;
        this.stickyFrame = requestAnimationFrame(() => {
          this.querySelector('[data-sticky]').hidden = this.button.getBoundingClientRect().bottom >= 0;
          this.stickyFrame = null;
        });
      };
      window.addEventListener('scroll', updateSticky, { ...opts, passive: true });
      window.addEventListener('resize', updateSticky, opts);
      this.observer = new ResizeObserver(updateSticky);
      this.observer.observe(this.form);
      updateSticky();
      this.renderOptions(this.quantity > 1);
      this.update();
    }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); cancelAnimationFrame(this.stickyFrame); }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100); }
    mixValues() { return this.mixInputs.map(input => input.value === '' ? NaN : Number(input.value)); }
    renderOptions(resetMix) {
      const container = this.querySelector('[data-color-options]');
      container.replaceChildren();
      const colorIndex = this.product.options.findIndex(option => ['color', 'colour'].includes(option.toLowerCase()));
      const colors = colorIndex < 0 ? [] : [...new Set(this.product.variants.map(variant => variant.options[colorIndex]))];
      if (colors.length && !colors.includes(this.color)) this.color = colors[0];
      this.querySelector('[data-single-color]').hidden = !colors.length;
      for (const color of colors) {
        const label = document.createElement('label'), input = document.createElement('input'), span = document.createElement('span');
        Object.assign(input, { type: 'radio', name: 'latimer-color', value: color, checked: color === this.color });
        input.setAttribute('aria-label', color); span.dataset.swatch = color.toLowerCase().replaceAll(' ', '-'); span.className = 'lg-color-dot'; span.setAttribute('aria-hidden', 'true'); label.title = color; label.append(input, span); container.append(label);
      }
      const mixing = this.everyday && this.quantity > 1;
      this.querySelector('[data-mix]').hidden = !mixing;
      this.mixInputs.forEach((input, index) => { input.disabled = !mixing; input.max = this.quantity; if (resetMix) input.value = input.dataset.mixColor === this.color ? this.quantity : 0; });
    }
    update() {
      if (this.busy) return;
      if (this.lastRenderedProduct !== this.product.id) {
        const title = this.querySelector('[data-product-title]');
        if (title) title.textContent = this.product.title;
        this.querySelector('[data-sticky-title]').textContent = this.product.title;
        const description = this.querySelector('[data-product-description]');
        if (description) description.innerHTML = this.product.descriptionHtml || '';
        this.lastRenderedProduct = this.product.id;
      }
      this.variant = matchVariant(this.product, { size: this.size.value, color: this.color });
      this.hiddenVariant.value = this.variant?.id || '';
      const priced = this.variant || this.product.variants[0];
      this.querySelector('[data-price]').textContent = priced?.priceText || '';
      this.querySelector('[data-compare]').textContent = priced?.compareText || '';
      this.querySelector('[data-pack-caption]').textContent = this.product.count === 1 ? '/ pair' : `/ ${this.product.count} pairs`;
      this.querySelector('[data-sticky-price]').textContent = priced?.priceText || '';
      this.querySelector('[data-mix-status]').textContent = `${this.mixValues().reduce((sum, n) => sum + (Number.isFinite(n) ? n : 0), 0)} of ${this.quantity} pairs selected`;
      this.querySelector('[data-color-name]').textContent = this.color;
      this.querySelectorAll('[name="latimer-color"]').forEach(input => { input.checked = input.value === this.color; });
      this.querySelector('[data-size-caption]').textContent = ({Medium: 'US men’s 6–8.5', Large: 'US men’s 9–12', 'X-Large': 'US men’s 13–16'})[this.size.value] || 'Choose your size';
      this.error.hidden = true;
      this.button.disabled = this.variant ? !this.variant.available : !this.product.variants.some(variant => variant.available);
      this.button.textContent = this.button.disabled ? (this.variant ? 'Sold out' : 'Unavailable') : 'Add to cart';
      this.querySelector('[data-sticky-add]').textContent = this.variant && !selectionError(this.product, this.variant, this.mixValues()) ? 'Add to cart' : 'Choose your options';
      const preorderVariants = this.preorderVariants();
      const preorder = preorderVariants.length > 0;
      const note = this.querySelector('[data-preorder-note]');
      note.hidden = !preorder;
      note.textContent = preorder ? this.offerLabels.preorderNote.replace('[variants]', preorderVariants.map(variant => variant.options.join(' / ')).join(', ')) : '';
      if (preorder && !this.button.disabled) {
        this.button.textContent = this.offerLabels.preorder;
        this.querySelector('[data-sticky-add]').textContent = this.offerLabels.preorder;
      }
      if (this.everyday) {
        const total = this.money((priced?.price || 0) * (this.quantity === 1 ? 1 : this.quantity / 2));
        const problem = this.purchaseError();
        this.button.disabled = Boolean(problem);
        this.button.textContent = problem || (preorder ? `${this.offerLabels.preorder} · ${total}` : `Add ${this.quantity} ${this.quantity === 1 ? 'pair' : 'pairs'} to cart · ${total}`);
        this.querySelector('[data-sticky-price]').textContent = total;
        this.querySelector('[data-sticky-add]').textContent = problem ? 'Choose your options' : this.button.textContent;
      }
      const gallery = this.querySelector('lg-carousel');
      const colorIndex = this.product.options.findIndex(option => ['color', 'colour'].includes(option.toLowerCase()));
      const colors = [...new Set(this.product.variants.map(variant => variant.options[colorIndex]).filter(Boolean))];
      if (this.product.count === 1) filterColorGallery(gallery, this.color, colors);
      else gallery?.querySelectorAll('[data-slide], [data-index]').forEach(element => { element.hidden = false; });
      if (this.lastColor !== this.color || this.lastPack !== this.product.id) gallery?.go(0, true);
      gallery?.update();
      this.lastColor = this.color; this.lastPack = this.product.id;
      const urlVariant = this.variant || this.product.variants.find(variant => variant.options[colorIndex] === this.color && variant.available) || this.product.variants.find(variant => variant.options[colorIndex] === this.color);
      if (urlVariant && this.product.id === this.data.current) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', urlVariant.id);
        window.history.replaceState(window.history.state, '', url);
      }

    }
    preorderVariants() {
      const selected = this.everyday && this.quantity > 1
        ? this.mixInputs.filter(input => Number(input.value) > 0).map(input => matchVariant(this.product, { size: this.size.value, color: input.dataset.mixColor }))
        : [this.variant];
      return selected.filter(isPreorder);
    }
    purchaseError() {
      if (!this.everyday) return selectionError(this.product, this.variant, this.mixValues());
      if (!this.size.value) return 'Choose your size';
      if (this.quantity === 1) return selectionError(this.product, this.variant, []);
      if (!validMix(this.mixValues(), this.quantity)) return `Choose exactly ${this.quantity} pairs`;
      return this.mixInputs.some(input => Number(input.value) > 0 && !matchVariant(this.product, { size: this.size.value, color: input.dataset.mixColor })?.available) ? 'Selected color is sold out in this size' : '';
    }
    cartItems() {
      if (this.quantity === 1) return [{ id: this.variant.id, quantity: 1 }];
      return this.mixInputs.filter(input => Number(input.value) > 0).map(input => ({ id: matchVariant(this.product, { size: this.size.value, color: input.dataset.mixColor }).id, quantity: Number(input.value) }));
    }
    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      const problem = this.purchaseError();
      if (problem) { this.error.textContent = problem; this.error.hidden = false; return; }
      this.busy = true; this.button.disabled = true; this.button.textContent = 'Adding…'; this.form.setAttribute('aria-busy', 'true');
      const cart = document.querySelector('cart-drawer');
      const data = new FormData(this.form);
      // UI controls never become order properties; native variant ID is the source of truth.
      data.delete('latimer-pack'); data.delete('latimer-color');
      if (cart) { data.set('sections', cart.getSectionsToRender().map(section => section.id).join(',')); data.set('sections_url', location.pathname); cart.setActiveElement(this.button); }
      data.delete('latimer-size');
      let body = data;
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      let added = false;
      try {
        if (this.everyday) {
          const selectedItems = this.cartItems();
          if (this.quantity > 1) kiteBogoItems(selectedItems, activeKiteData());
          const payload = { items: selectedItems.map(item => ({...item, properties:paidBogoProperties(item)})) };
          if (cart) { payload.sections = cart.getSectionsToRender().map(section => section.id); payload.sections_url = location.pathname; }
          body = JSON.stringify(payload); headers['Content-Type'] = 'application/json';
        }
        const add = async () => {
          const response = await fetch(window.routes.cart_add_url.replace(/\.js$/, '') + '.js', { method: 'POST', headers, body });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this selection. Please try again.');
          added = true;
          return result;
        };
        const result = this.everyday ? await withBogoCart(add, cart ? cart.getSectionsToRender().map(section => section.id) : []) : await add();
        this.querySelector('[data-status]').textContent = 'Added to cart.';
        if (cart && result.sections && Object.values(result.sections).every(Boolean)) { cart.classList.remove('is-empty'); cart.renderContents(result); }
        else location.assign(window.routes.cart_url);
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'latimer-product', productVariantId: this.variant.id, cartData: result });
      } catch (error) {
        // Once Shopify accepted the item, never offer a second submission because a drawer failed.
        if (added) location.assign(window.routes.cart_url);
        else { this.error.textContent = error.message; this.error.hidden = false; }
      } finally { const message = this.error.hidden ? '' : this.error.textContent; this.busy = false; this.form.removeAttribute('aria-busy'); this.update(); if (message) { this.error.textContent = message; this.error.hidden = false; } }
    }
  }
  class LatimerArticle extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      const body = this.querySelector('[data-article-content]');
      const toc = this.querySelector('[data-toc]');
      const list = toc.querySelector('ol'); list.replaceChildren();
      body.querySelectorAll('h2, h3').forEach((heading, i) => {
        if (!heading.id) heading.id = `entry-heading-${i + 1}`;
        const li = document.createElement('li'), link = document.createElement('a');
        link.href = `#${heading.id}`; link.textContent = heading.textContent; li.append(link); list.append(li);
      });
      toc.hidden = !list.children.length;
      this.querySelector('[data-reading-time]').textContent = `${Math.max(1, Math.ceil(body.textContent.trim().split(/\s+/).length / 220))} min read`;
      const update = () => {
        const rect = body.getBoundingClientRect();
        const fraction = Math.max(0, Math.min(1, (innerHeight * 0.25 - rect.top) / Math.max(1, rect.height - innerHeight * 0.5)));
        this.querySelector('[data-progress]').style.transform = `scaleX(${fraction})`;
      };
      window.addEventListener('scroll', update, { passive: true, signal: this.abort.signal }); update();
    }
    disconnectedCallback() { this.abort?.abort(); }
  }
  for (const [name, component] of [['lg-carousel', LatimerCarousel], ['lg-product', LatimerProduct], ['lg-article', LatimerArticle]]) if (!customElements.get(name)) customElements.define(name, component);
  // Header groups can be re-rendered independently by the theme editor.
  document.addEventListener('shopify:section:load', event => {
    if (event.target.querySelector('#cart-icon-bubble')) document.querySelector('cart-drawer')?.setHeaderCartIconAccessibility();
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') document.querySelectorAll('.lg-mobile-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); }); });
}



